import { Skeleton } from "@/components/shared/Skeleton";
import { useT } from "@/i18n";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { HiChevronRight } from "react-icons/hi";
import { CLASSROOM_INDEX, CLASSROOM_ROOT, type ClassroomScope } from "./classroomScope";

/** Schools or Classrooms › classroom › student › whatever the page adds. Only the tail is plain. */
export function StudentBreadcrumb({
  scope,
  classroomId,
  classroomName,
  studentId,
  studentName,
  trail = [],
}: {
  scope: ClassroomScope;
  classroomId: string;
  classroomName: string | null;
  studentId: string;
  studentName: string | null;
  /** Crumbs after the student, innermost last. A page links its own where it wants to. */
  trail?: ReactNode[];
}) {
  const t = useT();
  const root = CLASSROOM_ROOT[scope];
  const index = CLASSROOM_INDEX[scope];

  return (
    <nav
      aria-label={t["nav.breadcrumb"]}
      className="flex items-center gap-1 body-sm min-w-0 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <Link to={index.to} className="text-app-ink/60 hover:text-app-ink shrink-0">
        {t[index.labelKey]}
      </Link>
      <HiChevronRight className="w-4 h-4 text-app-ink/40 shrink-0" />
      {classroomName === null ? (
        <Skeleton className="h-4 w-28 shrink-0" />
      ) : (
        <Link
          to={`${root}/$classroomId`}
          params={{ classroomId }}
          className="text-app-ink/60 hover:text-app-ink shrink-0 sm:shrink sm:truncate"
        >
          {classroomName}
        </Link>
      )}
      <HiChevronRight className="w-4 h-4 text-app-ink/40 shrink-0" />
      {studentName === null ? (
        <Skeleton className="h-4 w-24 shrink-0" />
      ) : trail.length === 0 ? (
        <span className="font-semibold text-app-ink shrink-0 sm:shrink sm:truncate">
          {studentName}
        </span>
      ) : (
        <Link
          to={`${root}/$classroomId/students/$studentId`}
          params={{ classroomId, studentId }}
          className="text-app-ink/60 hover:text-app-ink shrink-0 sm:shrink sm:truncate"
        >
          {studentName}
        </Link>
      )}
      {trail.map((crumb, index) => {
        // A page's trail is fixed and never reorders, so position is a crumb's identity.
        const crumbKey = `crumb-${index}`;
        const isLast = index === trail.length - 1;
        return (
          <span key={crumbKey} className="flex items-center gap-1 min-w-0 shrink-0 sm:shrink">
            <HiChevronRight className="w-4 h-4 text-app-ink/40 shrink-0" />
            <span
              className={
                isLast
                  ? "font-semibold text-app-ink shrink-0 sm:shrink sm:truncate"
                  : "text-app-ink/60 shrink-0 sm:shrink sm:truncate"
              }
            >
              {crumb}
            </span>
          </span>
        );
      })}
    </nav>
  );
}
